<script lang="ts" module>
  import {
    generateId,
    getUiclContext,
    useActions,
    type HTMLActionArray,
  } from "#lib/internal/helpers"
  import { onMount, tick, type Snippet } from "svelte"
  import { getSelectRootContext, type Option } from "./SelectRoot.svelte"

  type CheckboxAlignment = "left" | "right" | "hidden"

  type Props = Omit<BaseProps<"li">, "value"> & {
    // mimic option attributes
    label?: string
    value: unknown
    disabled?: boolean
    // extra attributes
    use?: HTMLActionArray
    class?: ClassValue
    checkboxClass?: string
    checkboxAlignment?: CheckboxAlignment
    children?: Snippet
    content?: Snippet
    onclick?: (event: MouseEvent) => void
  }
</script>

<script lang="ts">
  import { selectOptionVariants } from "#lib/components/ui/select/variants"
  import type { BaseProps } from "#lib/internal/types"
  import { cn } from "#lib/utils"
  import { Check, Checkbox, CheckboxFilled } from "@tastyworks/icons-2.0"
  import type { ClassValue } from "clsx"
  import Content from "./SelectContent.svelte"

  let {
    label = "",
    disabled = false,
    checkboxAlignment = "left",
    checkboxClass = "",
    value,
    class: className = "",
    children,
    content,
    onclick,
    use = [],
    ...restProps
  }: Props = $props()

  const rootCtx = getSelectRootContext()

  let id = generateId(`${rootCtx.idPrefix}-option`, "")

  let optionRef: HTMLLIElement | null = $state(null)
  let innerText: string = $state("")
  // Store the raw value reference — not a $state.snapshot — so that
  // boulangerie/Java objects preserve their class identity when passed
  // through onchange to consumers.
  let option: Option = { label, value, id }

  $effect(() => {
    option.label = innerText || label
  })

  // Derived index re-evaluates when rootCtx.options changes (e.g. after
  // registerOption/unRegisterOption). This is O(n) per option but only
  // runs when the options array is reassigned.
  const index = $derived(rootCtx.options.findIndex((o) => o.id === id))
  const highlighted = $derived(index === rootCtx.highlightedIndex)
  const kbHighlighted = $derived(index === rootCtx.keyboardHighlightedIndex)

  $effect(() => {
    if (highlighted) {
      ;(async function () {
        await tick()
        optionRef?.scrollIntoView({ block: "nearest" })
      })()
    }
  })

  // See SelectRoot.valuesEqual for explanation of the guard structure.
  function valuesEqual(a: unknown, b: unknown): boolean {
    if (a == null || b == null) return a === b
    if (typeof a !== "object" && typeof b !== "object") return a === b
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      return a === b
    }
  }

  const selected = $derived(
    Array.isArray(rootCtx.selected)
      ? rootCtx.selected.some((o) => valuesEqual(o.value, value))
      : valuesEqual(rootCtx.value, value)
  )
  const CheckboxComponent = $derived(selected ? CheckboxFilled : Checkbox)

  const v2 = !!getUiclContext()?.v2

  function onMouseEnter() {
    rootCtx.highlight(index, false)
  }

  function onMouseLeave() {
    if (rootCtx.highlightedIndex === index) {
      rootCtx.highlight(-1, false)
    }
  }

  onMount(() => {
    rootCtx.registerOption(option)
    return () => rootCtx.unRegisterOption(option)
  })
</script>

<li
  bind:this={optionRef}
  onclick={(event) => {
    rootCtx.select(index)
    if (!rootCtx.multiselect) {
      rootCtx.closeSelectPanel()
    }
    onclick?.(event)
  }}
  onmouseleave={onMouseLeave}
  onmouseenter={onMouseEnter}
  role="option"
  aria-selected={selected}
  class:pointer-events-none={disabled}
  data-highlighted={highlighted}
  data-kb-highlighted={kbHighlighted}
  data-value={value}
  {...restProps}
  {id}
  class={cn(selectOptionVariants({ size: rootCtx.size, v2 }), className)}
  use:useActions={use}
>
  {#if rootCtx.multiselect && checkboxAlignment === "left"}
    <CheckboxComponent class={cn("aspect-square size-5", checkboxClass)} />
  {/if}
  {#if content}
    {@render content()}
  {:else}
    <Content bind:innerText class="whitespace-normal">
      {#if label}
        {label}
      {:else}
        {@render children?.()}
      {/if}
    </Content>
  {/if}
  {#if !rootCtx.multiselect && valuesEqual(rootCtx.value, value) && checkboxAlignment !== "hidden"}
    <Check />
  {:else if rootCtx.multiselect && checkboxAlignment === "right"}
    <CheckboxComponent class={cn("aspect-square size-5", checkboxClass)} />
  {/if}
</li>
